import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { PublicClientApplication, AccountInfo } from '@azure/msal-browser';
import { msalConfig, loginRequest } from '../config/authConfig';
import { StorageService } from '../services/storageService';
import { AdminService } from '../services/adminService';
import { TokenCredential } from '@azure/identity';

interface AuthContextType {
  isAuthenticated: boolean;
  account: AccountInfo | null;
  login: () => Promise<void>;
  logout: () => void;
  storageService: StorageService | null;
  adminService: AdminService | null;
  isInitializing: boolean;
  isAdmin: boolean;
  isCheckingAdmin: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Custom TokenCredential implementation that uses MSAL
class MSALTokenCredential implements TokenCredential {
  private msalInstance: PublicClientApplication;
  
  constructor(msalInstance: PublicClientApplication) {
    this.msalInstance = msalInstance;
  }

  async getToken(scopes: string | string[]): Promise<any> {
    const scopesArray = typeof scopes === 'string' ? [scopes] : scopes;
    const account = this.msalInstance.getAllAccounts()[0];
    
    if (!account) {
      throw new Error('No active account! Please sign in before accessing token.');
    }
    
    try {
      // Try silent token acquisition first
      const response = await this.msalInstance.acquireTokenSilent({
        scopes: scopesArray,
        account: account
      });
      return { token: response.accessToken, expiresOnTimestamp: response.expiresOn?.getTime() };
    } catch (error: any) {
      // Fall back to interactive acquisition whenever silent (hidden-iframe)
      // renewal can't complete. Besides InteractionRequiredAuthError, this covers
      // monitor_window_timeout — the iframe token renewal timing out, which is
      // common when the browser blocks third-party cookies for Microsoft login.
      const code = error?.errorCode || '';
      const needsInteraction =
        error?.name === 'InteractionRequiredAuthError' ||
        code === 'monitor_window_timeout' ||
        code === 'interaction_required' ||
        code === 'login_required' ||
        code === 'consent_required';
      if (!needsInteraction) {
        throw error;
      }

      // Check for ongoing interactions to prevent duplicate popups
      try {
        const response = await this.msalInstance.acquireTokenPopup({
          scopes: scopesArray,
          account: account
        });
        return { token: response.accessToken, expiresOnTimestamp: response.expiresOn?.getTime() };
      } catch (popupError: any) {
        if (popupError.errorCode === 'interaction_in_progress') {
          console.log('Authentication already in progress. Please complete the current authentication flow.');
        }
        throw popupError;
      }
    }
  }
}

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [account, setAccount] = useState<AccountInfo | null>(null);
  const [msalInstance, setMsalInstance] = useState<PublicClientApplication | null>(null);
  const [storageService, setStorageService] = useState<StorageService | null>(null);
  const [isInitializing, setIsInitializing] = useState<boolean>(true);
  const [isInProgress, setIsInProgress] = useState<boolean>(false);
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [isCheckingAdmin, setIsCheckingAdmin] = useState<boolean>(false);
  const [adminService, setAdminService] = useState<AdminService | null>(null);
  const navigate = useNavigate();

  // Initialize MSAL
  useEffect(() => {
    const initializeMsal = async () => {
      setIsInitializing(true);
      try {
        const instance = new PublicClientApplication(msalConfig);
        await instance.initialize();

        // Handle any redirect responses at startup. A non-null result means a
        // sign-in redirect just completed → send the admin to the dashboard.
        const redirectResult = await instance.handleRedirectPromise();

        setMsalInstance(instance);

        const accounts = instance.getAllAccounts();
        if (accounts.length > 0) {
          setAccount(accounts[0]);
          setIsAuthenticated(true);
          // Store account info for admin API calls
          sessionStorage.setItem('msalAccount', JSON.stringify(accounts[0]));
          const credential = new MSALTokenCredential(instance);
          setStorageService(new StorageService(credential));
          setAdminService(new AdminService());
        }

        if (redirectResult && redirectResult.account) {
          navigate('/admin');
        }
      } catch (error) {
        console.error('Failed to initialize MSAL:', error);
      } finally {
        setIsInitializing(false);
      }
    };

    initializeMsal();
  }, []);

  // Ensure admin entry exists and check admin status when authenticated
  useEffect(() => {
    const checkAdminStatus = async () => {
      console.log('🔍 checkAdminStatus called:', {
        isAuthenticated,
        hasAccount: !!account,
        hasAdminService: !!adminService,
        accountEmail: account?.username,
        accountObjectId: account?.localAccountId || account?.homeAccountId
      });
      
      if (isAuthenticated && account && adminService) {
        setIsCheckingAdmin(true);
        try {
          const userObjectId = account.localAccountId || account.homeAccountId || '';
          const userEmail = account.username || '';
          
          console.log('📝 Calling ensureAdminEntry with:', {
            userObjectId,
            userEmail,
            hasUserObjectId: !!userObjectId,
            hasUserEmail: !!userEmail
          });
          
          // First, ensure admin entry exists (creates with isAdmin: false if new)
          await adminService.ensureAdminEntry(userObjectId, userEmail);
          
          console.log('✅ ensureAdminEntry completed');
          
          // Then check if user has admin access
          const adminStatus = await adminService.isAdmin(userObjectId, userEmail);
          console.log('📊 Admin status result:', adminStatus);
          setIsAdmin(adminStatus);
        } catch (error) {
          console.error('❌ Error checking admin status:', error);
          setIsAdmin(false);
        } finally {
          setIsCheckingAdmin(false);
        }
      } else {
        console.log('⚠️ Skipping admin check - missing requirements:', {
          isAuthenticated,
          hasAccount: !!account,
          hasAdminService: !!adminService
        });
        setIsAdmin(false);
        setIsCheckingAdmin(false);
      }
    };

    checkAdminStatus();
  }, [isAuthenticated, account, adminService]);

  // Check if an authentication interaction is in progress
  const checkForInProgressInteraction = (): boolean => {
    if (!msalInstance) return false;
    
    try {
      // This is a basic check that can help identify if interaction is in progress
      const accounts = msalInstance.getAllAccounts();
      return accounts.length === 0 && isInProgress;
    } catch (error) {
      console.error('Error checking for in-progress interaction:', error);
      return false;
    }
  };

  const login = async (): Promise<void> => {
    if (!msalInstance) return;
    
    // Prevent multiple login attempts
    if (checkForInProgressInteraction()) {
      console.log('Authentication already in progress. Please complete the current flow.');
      return;
    }
    
    try {
      setIsInProgress(true);

      // If already signed in, just wire up the services and go.
      const accounts = msalInstance.getAllAccounts();
      if (accounts.length > 0) {
        setAccount(accounts[0]);
        setIsAuthenticated(true);
        sessionStorage.setItem('msalAccount', JSON.stringify(accounts[0]));
        const credential = new MSALTokenCredential(msalInstance);
        setStorageService(new StorageService(credential));
        setAdminService(new AdminService());
        navigate('/admin');
        return;
      }

      // Otherwise sign in via a full-page redirect. This avoids popup blockers
      // and the hidden-iframe issues (monitor_window_timeout) that break the
      // popup flow. The response is handled by handleRedirectPromise on return.
      await msalInstance.loginRedirect(loginRequest);
    } catch (error: any) {
      if (error.errorCode === 'interaction_in_progress') {
        console.log('Authentication already in progress. Please complete the current authentication flow.');
      } else {
        console.error('Login failed:', error);
      }
    } finally {
      setIsInProgress(false);
    }
  };

  const logout = (): void => {
    if (!msalInstance) return;
    
    msalInstance.logout();
    setIsAuthenticated(false);
    setAccount(null);
    setStorageService(null);
    setAdminService(null);
    setIsAdmin(false);
    // Clear stored account info
    sessionStorage.removeItem('msalAccount');
  };

  // Auto sign-out after 15 minutes of inactivity (admin dashboard). Activity is
  // shared across tabs via localStorage so any active tab keeps the session alive.
  useEffect(() => {
    if (!isAuthenticated) return;
    const IDLE_MS = 15 * 60 * 1000;
    const ACTIVITY_KEY = 'lastActivity';

    let lastMark = 0;
    const markActivity = () => {
      const now = Date.now();
      if (now - lastMark > 5000) {
        lastMark = now;
        localStorage.setItem(ACTIVITY_KEY, now.toString());
      }
    };
    localStorage.setItem(ACTIVITY_KEY, Date.now().toString());

    const events = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    events.forEach((e) => window.addEventListener(e, markActivity, { passive: true }));

    const interval = setInterval(() => {
      const la = parseInt(localStorage.getItem(ACTIVITY_KEY) || '0', 10);
      if (la && Date.now() - la > IDLE_MS) {
        logout();
      }
    }, 20000);

    return () => {
      events.forEach((e) => window.removeEventListener(e, markActivity));
      clearInterval(interval);
    };
  }, [isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <AuthContext.Provider value={{ isAuthenticated, account, login, logout, storageService, adminService, isInitializing, isAdmin, isCheckingAdmin }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 